type ExtensionOpenTabsResult = {
  opened: number
  failed: number
}

const OPEN_TABS_REQUEST = 'artem-extension:open-eurlex-tabs'
const OPEN_TABS_ACK = 'artem-extension:open-eurlex-tabs:ack'
const OPEN_TABS_RESPONSE = 'artem-extension:open-eurlex-tabs:result'
const BRIDGE_ACK_TIMEOUT_MS = 250
const EXTENSION_RESPONSE_BASE_TIMEOUT_MS = 1500
const EXTENSION_RESPONSE_PER_URL_TIMEOUT_MS = 300
const EXTENSION_RESPONSE_MAX_TIMEOUT_MS = 15000

export async function openEurLexUrlsViaExtension(
  urls: string[]
): Promise<ExtensionOpenTabsResult | null> {
  if (urls.length === 0) {
    return { opened: 0, failed: 0 }
  }

  const requestId = `req_${Date.now()}_${Math.random().toString(36).slice(2)}`
  const responseTimeoutMs = Math.min(
    EXTENSION_RESPONSE_MAX_TIMEOUT_MS,
    EXTENSION_RESPONSE_BASE_TIMEOUT_MS +
      urls.length * EXTENSION_RESPONSE_PER_URL_TIMEOUT_MS
  )

  return await new Promise<ExtensionOpenTabsResult | null>((resolve) => {
    let responseTimeoutId: number | null = null
    const ackTimeoutId = window.setTimeout(() => {
      settle(null)
    }, BRIDGE_ACK_TIMEOUT_MS)

    let settled = false
    let ackReceived = false
    const cleanup = () => {
      window.clearTimeout(ackTimeoutId)
      if (responseTimeoutId !== null) {
        window.clearTimeout(responseTimeoutId)
      }
      window.removeEventListener('message', onMessage)
    }
    const settle = (value: ExtensionOpenTabsResult | null) => {
      if (settled) {
        return
      }
      settled = true
      cleanup()
      resolve(value)
    }

    const scheduleResponseTimeout = () => {
      if (responseTimeoutId !== null) {
        window.clearTimeout(responseTimeoutId)
      }
      responseTimeoutId = window.setTimeout(() => {
        settle(null)
      }, responseTimeoutMs)
    }

    const onMessage = (event: MessageEvent) => {
      if (event.source !== window) {
        return
      }
      const messageType = event.data?.type
      if (messageType !== OPEN_TABS_ACK && messageType !== OPEN_TABS_RESPONSE) {
        return
      }
      if (event.data?.requestId !== requestId) {
        return
      }
      if (messageType === OPEN_TABS_ACK) {
        ackReceived = true
        window.clearTimeout(ackTimeoutId)
        scheduleResponseTimeout()
        return
      }
      if (!ackReceived) {
        // Backward compatibility for older extension versions that respond without an ack.
        window.clearTimeout(ackTimeoutId)
        scheduleResponseTimeout()
      }
      if (!event.data?.ok) {
        settle(null)
        return
      }
      const opened = Number(event.data?.result?.opened ?? 0)
      const failed = Number(event.data?.result?.failed ?? 0)
      settle({
        opened: Number.isFinite(opened) ? opened : 0,
        failed: Number.isFinite(failed) ? failed : 0,
      })
    }

    window.addEventListener('message', onMessage)
    window.postMessage(
      {
        type: OPEN_TABS_REQUEST,
        requestId,
        urls,
      },
      '*'
    )
  })
}
